import { AppBar, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { FC, memo, useContext, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useLocation, useNavigate, useParams } from 'react-router';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import LicenseContext from 'src/contexts/LicenseContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { CompanyEntity } from 'src/entities/admin/companyEntity';
import { endOnForSpan, LONG_HAUL_DEFAULT_SPAN_DAYS } from 'src/pages/LongHaul/spanDays';

import AppBarLgMenuComponent from './AppBarLgMenu.component';
import AppBarLogoComponent from './AppBarLogo.component';
import AppBarMdMenuComponent from './AppBarMdMenu.component';
import AppBarSmMenuComponent from './AppBarSmMenu.component';

const AppBarContainerComponent: FC = memo(() => {
  const licenseContext = useContext(LicenseContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const navigate = useNavigate();
  const { startOn, endOn } = useParams();
  const { pathname } = useLocation();

  // 長距離配車だけ既定が 7 日レンジなので、出入りで終了日の扱いを変える必要がある。
  // Why not window.location.pathname: 画面遷移しても再描画が起きず、前の画面の判定のまま残る。
  // Why not useParams: path param は日付しか持たず、どの画面にいるかは分からない
  const isOnLongHaul = pathname === '/long-haul' || pathname.startsWith('/long-haul/');

  const [hideThisComponent, setHideThisComponent] = useState<boolean>(false);
  const [hideShowAllSelectMenuItem, setHideShowAllSelectMenuItem] = useState<boolean>(false);
  const [companies, setCompanies] = useState<CompanyEntity[]>([]);

  const [planningMenuAnchorEl, setPlanningMenuAnchorEl] = useState<null | HTMLElement>(null);
  const handlePlanningMenuOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPlanningMenuAnchorEl(event.currentTarget);
  };
  const handlePlanningMenuOnClose = () => {
    setPlanningMenuAnchorEl(null);
  };

  const [settingMenuAnchorEl, setSettingMenuAnchorEl] = useState<null | HTMLElement>(null);
  const handleSettingMenuOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSettingMenuAnchorEl(event.currentTarget);
  };
  const handleSettingMenuOnClose = () => {
    setSettingMenuAnchorEl(null);
  };

  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState<null | HTMLElement>(null);
  const handleAccountMenuOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAccountMenuAnchorEl(event.currentTarget);
  };
  const handleAccountMenuOnClose = () => {
    setAccountMenuAnchorEl(null);
  };

  const buildPathWithStartOnAndEndOn = (plansOrTimelineOrOrders: string) => {
    if (startOn && endOn) {
      return `/${plansOrTimelineOrOrders}/${startOn}/${endOn}`;
    }

    if (licenseContext.startOn && licenseContext.endOn) {
      return `/${plansOrTimelineOrOrders}/${licenseContext.startOn}/${licenseContext.endOn}`;
    }

    const on = localStorage.getItem('planningOn') || datetimeDecorator.toYyyyMmDd(new Date());
    return `/${plansOrTimelineOrOrders}/${on}/${on}`;
  };

  const buildPathWithStartOn = (plansOrTimelineOrOrders: string) => {
    if (startOn) {
      return `/${plansOrTimelineOrOrders}/${startOn}`;
    }

    if (licenseContext.startOn) {
      return `/${plansOrTimelineOrOrders}/${licenseContext.startOn}`;
    }

    const on = localStorage.getItem('planningOn') || datetimeDecorator.toYyyyMmDd(new Date());
    return `/${plansOrTimelineOrOrders}/${on}`;
  };

  /** いま表示している開始日。拾う順番は buildPathWithStartOn と同じ */
  const currentStartOn = () => (
    startOn
    || licenseContext.startOn
    || localStorage.getItem('planningOn')
    || datetimeDecorator.toYyyyMmDd(new Date())
  );

  /**
   * 1 日単位で見る配車画面（マップ・タイムライン・カード・レーン管理）へのリンク。
   *
   * 長距離配車からの遷移だけ開始日＝終了日に畳む。7 日レンジをそのまま渡すと、遷移先は
   * その期間の全案件を一度に抱えることになり、当日ぶんを見たいだけの画面が重くなる。
   * Why not 一覧系（案件一覧・案件管理・配車一覧）も畳む: あちらはレンジ表示が本来の
   * 使い方なので、長距離配車の 7 日をそのまま渡した方が期待に合う
   */
  const buildDispatchPath = (plansOrTimeline: string) => {
    if (!isOnLongHaul) {
      return buildPathWithStartOnAndEndOn(plansOrTimeline);
    }

    const on = currentStartOn();
    return `/${plansOrTimeline}/${on}/${on}`;
  };

  const navigateToDashboard = (isLogo: boolean) => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: isLogo ? 'ロゴ' : SCREEN_NAMES.DASHBOARD });
    void navigate('/dashboard');
  };

  const navigateToKpis = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '実績参照' });
    void navigate('/kpis');
  };

  const navigateToPlanningMap = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - マップ' });
    void navigate(buildDispatchPath('plans'));
    handlePlanningMenuOnClose();
  };

  const navigateToMap = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - 地図(新)' });
    void navigate(buildDispatchPath('map'));
    handlePlanningMenuOnClose();
  };

  const navigateToPlanningTimeline = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - タイムライン' });
    void navigate(buildDispatchPath('timeline'));
    handlePlanningMenuOnClose();
  };

  const navigateToPlanningLane = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - レーン管理' });
    void navigate(buildDispatchPath('lanes'));
    handlePlanningMenuOnClose();
  };

  const navigateToDispatchTableImport = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - 配車表取込' });
    void navigate('/dispatch-table-imports');
    handlePlanningMenuOnClose();
  };

  const navigateToConvenienceDispatches = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - 固定コース' });
    void navigate(buildDispatchPath('convenience-dispatches'));
    handlePlanningMenuOnClose();
  };

  /**
   * 長距離配車へのリンク。開いていた画面の開始日を起点に、終了日だけ 7 日ぶんへ広げる。
   *
   * Why not レンジをそのまま引き継ぐ: 他画面は 1 日レンジなので、長距離の運行が 1 日ぶん
   * しか見えなくなる。
   * Why not パラメータ無しで画面側のフォールバックに任せる: 開始日まで捨てることになり、
   * 8/31 を見ていても config の既定日（当日など）から開いてしまう
   */
  const buildLongHaulPath = () => {
    const on = currentStartOn();
    return `/long-haul/${on}/${endOnForSpan(on, LONG_HAUL_DEFAULT_SPAN_DAYS)}`;
  };

  const navigateToLongHaul = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: `配車計画 - ${SCREEN_NAMES.PLANNING_LONG_HAUL}` });
    void navigate(buildLongHaulPath());
    handlePlanningMenuOnClose();
  };

  const navigateToPlanningTrunkTransportation = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車計画 - 幹線輸送' });
    void navigate(buildPathWithStartOn('trunk-transportation'));
    handlePlanningMenuOnClose();
  };

  const navigateToOrders = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '案件一覧' });
    void navigate(buildPathWithStartOnAndEndOn('orders'));
  };

  const navigateToSalesOrders = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '受注一覧' });
    void navigate('/sales-orders');
  };

  const navigateToShipmentOrders = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '案件管理' });
    void navigate(buildPathWithStartOnAndEndOn('shipment-orders'));
  };

  const navigateToShipmentOrderLegs = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '配車一覧' });
    void navigate(buildPathWithStartOnAndEndOn('shipment-order-legs'));
  };

  const navigateToCourses = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - ダイヤ・コースマスタ' });
    void navigate('/courses');
    handleSettingMenuOnClose();
  };

  const navigateToContracts = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 契約管理' });
    void navigate('/contracts');
    handleSettingMenuOnClose();
  };

  const navigateToGarages = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 車庫管理' });
    void navigate('/garages');
    handleSettingMenuOnClose();
  };

  const navigateToPartnerCompanies = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 運送会社管理' });
    void navigate('/partner-companies');
    handleSettingMenuOnClose();
  };

  const navigateToTrucks = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 車両管理' });
    void navigate('/trucks');
    handleSettingMenuOnClose();
  };

  const navigateToGroups = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 車両グループ管理' });
    void navigate('/groups');
    handleSettingMenuOnClose();
  };

  const navigateToDrivers = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - ドライバー管理' });
    void navigate('/drivers');
    handleSettingMenuOnClose();
  };

  const navigateToPlaces = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 地点管理' });
    void navigate('/places');
    handleSettingMenuOnClose();
  };

  const navigateToProducts = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 商品管理' });
    void navigate('/products');
    handleSettingMenuOnClose();
  };

  const navigateToAreas = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - エリア管理' });
    void navigate('/areas');
    handleSettingMenuOnClose();
  };

  const navigateToOutsourcingContractSettings = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - 委託表設定' });
    void navigate('/outsourcing-contract-settings');
    handleSettingMenuOnClose();
  };

  const navigateToConfig = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: '設定 - システム設定' });
    void navigate('/config');
    handleSettingMenuOnClose();
  };

  const navigateToPerformanceManagement = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: SCREEN_NAMES.PERFORMANCE_MANAGEMENT });
    void navigate('/performance-management');
  };

  const companyName = () => licenseContext?.config?.company_name;

  const userName = () => licenseContext?.config?.person_name || licenseContext?.config?.email;

  const useTrunkTransportation = licenseContext?.config?.use_trunk_transportation;
  const useLogForSales = licenseContext?.config?.use_log_for_sales;
  const useOutsourcingContractPdf = licenseContext.config?.use_outsourcing_contract_pdf;
  const useDispatchTableImport = licenseContext.config?.use_dispatch_table_import;

  const navigateToAccounts = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: 'アカウント - アカウント情報' });
    void navigate('/accounts');
    handleAccountMenuOnClose();
  };

  const isAdmin = () => licenseContext?.config?.role && licenseContext?.config?.role?.name === 'admin';

  const navigateToCompany = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: 'アカウント - 会社情報' });
    void navigate('/company');
    handleAccountMenuOnClose();
  };

  const logout = () => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.HEADER, button_name: 'アカウント - ログアウト' });
    licenseContext.logout();
    handleAccountMenuOnClose();
  };

  useEffect(() => {
    const hideShowAllSelectMenuItemPaths = [
      '/config',
      '/import'
    ];

    setHideShowAllSelectMenuItem(
      hideShowAllSelectMenuItemPaths.includes(window.location.pathname)
    );
  }, [window.location.pathname]);

  useEffect(() => {
    const hideThisComponentPaths = [
      '/accounts',
      '/company',
    ];

    const isHideThisComponentPath = hideThisComponentPaths.includes(window.location.pathname);
    const notHaveSelectableCompanies = companies.length < 2;

    setHideThisComponent(isHideThisComponentPath || notHaveSelectableCompanies);
  }, [companies, window.location.pathname]);

  useEffect(() => {
    setCompanies(
      licenseContext?.config?.selectable_companies ?? []
    );
  }, [licenseContext]);
  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: theme.colors.alpha.white[100],
      }}
    >
      {(isLargeScreen) && (
        <Toolbar
          sx={{
            display: {
              xs: 'none',
              sm: 'none',
              md: 'none',
              lg: 'flex',
              xl: 'flex',
            },
          }}
        >
          <AppBarLogoComponent
            companies={companies}
            navigateToDashboard={navigateToDashboard}
            hideShowAllSelectMenuItem={hideShowAllSelectMenuItem}
            hideThisComponent={hideThisComponent}
          />
          <AppBarLgMenuComponent
            navigateToDashboard={navigateToDashboard}
            navigateToKpis={navigateToKpis}
            handlePlanningMenuOnClick={handlePlanningMenuOnClick}
            planningMenuAnchorEl={planningMenuAnchorEl}
            handlePlanningMenuOnClose={handlePlanningMenuOnClose}
            navigateToPlanningMap={navigateToPlanningMap}
            navigateToMap={navigateToMap}
            navigateToPlanningTimeline={navigateToPlanningTimeline}
            navigateToConvenienceDispatches={navigateToConvenienceDispatches}
            navigateToPlanningLane={navigateToPlanningLane}
            navigateToOrders={navigateToOrders}
            handleSettingMenuOnClick={handleSettingMenuOnClick}
            settingMenuAnchorEl={settingMenuAnchorEl}
            handleSettingMenuOnClose={handleSettingMenuOnClose}
            navigateToCourses={navigateToCourses}
            navigateToContracts={navigateToContracts}
            navigateToGarages={navigateToGarages}
            navigateToPartnerCompanies={navigateToPartnerCompanies}
            navigateToTrucks={navigateToTrucks}
            navigateToGroups={navigateToGroups}
            navigateToDrivers={navigateToDrivers}
            navigateToPlaces={navigateToPlaces}
            navigateToProducts={navigateToProducts}
            navigateToAreas={navigateToAreas}
            useOutsourcingContractPdf={useOutsourcingContractPdf}
            navigateToOutsourcingContractSettings={navigateToOutsourcingContractSettings}
            navigateToConfig={navigateToConfig}
            handleAccountMenuOnClick={handleAccountMenuOnClick}
            companyName={companyName}
            userName={userName}
            accountMenuAnchorEl={accountMenuAnchorEl}
            handleAccountMenuOnClose={handleAccountMenuOnClose}
            navigateToAccounts={navigateToAccounts}
            isAdmin={isAdmin}
            navigateToCompany={navigateToCompany}
            logout={logout}
            useTrunkTransportation={useTrunkTransportation}
            navigateToPlanningTrunkTransportation={navigateToPlanningTrunkTransportation}
            navigateToLongHaul={navigateToLongHaul}
            useLogForSales={useLogForSales}
            navigateToSalesOrders={navigateToSalesOrders}
            navigateToShipmentOrders={navigateToShipmentOrders}
            navigateToShipmentOrderLegs={navigateToShipmentOrderLegs}
            appEnv={licenseContext.config?.app_env}
            navigateToPerformanceManagement={navigateToPerformanceManagement}
            useDispatchTableImport={useDispatchTableImport}
            navigateToDispatchTableImport={navigateToDispatchTableImport}
          />
        </Toolbar>
      )}
      {(isMediumScreen) && (
        <Toolbar
          sx={{
            display: {
              xs: 'none',
              sm: 'flex',
              md: 'flex',
              lg: 'none',
              xl: 'none',
            }
          }}
        >
          <AppBarLogoComponent
            companies={companies}
            navigateToDashboard={navigateToDashboard}
            hideShowAllSelectMenuItem={hideShowAllSelectMenuItem}
            hideThisComponent={hideThisComponent}
          />
          <AppBarMdMenuComponent
            navigateToKpis={navigateToKpis}
            handlePlanningMenuOnClick={handlePlanningMenuOnClick}
            planningMenuAnchorEl={planningMenuAnchorEl}
            handlePlanningMenuOnClose={handlePlanningMenuOnClose}
            navigateToPlanningMap={navigateToPlanningMap}
            navigateToMap={navigateToMap}
            navigateToPlanningTimeline={navigateToPlanningTimeline}
            navigateToConvenienceDispatches={navigateToConvenienceDispatches}
            navigateToPlanningLane={navigateToPlanningLane}
            navigateToOrders={navigateToOrders}
            handleSettingMenuOnClick={handleSettingMenuOnClick}
            settingMenuAnchorEl={settingMenuAnchorEl}
            handleSettingMenuOnClose={handleSettingMenuOnClose}
            navigateToCourses={navigateToCourses}
            navigateToContracts={navigateToContracts}
            navigateToGarages={navigateToGarages}
            navigateToPartnerCompanies={navigateToPartnerCompanies}
            navigateToTrucks={navigateToTrucks}
            navigateToGroups={navigateToGroups}
            navigateToDrivers={navigateToDrivers}
            navigateToPlaces={navigateToPlaces}
            navigateToProducts={navigateToProducts}
            navigateToAreas={navigateToAreas}
            useOutsourcingContractPdf={useOutsourcingContractPdf}
            navigateToOutsourcingContractSettings={navigateToOutsourcingContractSettings}
            navigateToConfig={navigateToConfig}
            handleAccountMenuOnClick={handleAccountMenuOnClick}
            accountMenuAnchorEl={accountMenuAnchorEl}
            handleAccountMenuOnClose={handleAccountMenuOnClose}
            navigateToAccounts={navigateToAccounts}
            isAdmin={isAdmin}
            navigateToCompany={navigateToCompany}
            logout={logout}
            useTrunkTransportation={useTrunkTransportation}
            navigateToPlanningTrunkTransportation={navigateToPlanningTrunkTransportation}
            navigateToLongHaul={navigateToLongHaul}
            useLogForSales={useLogForSales}
            navigateToSalesOrders={navigateToSalesOrders}
            navigateToShipmentOrders={navigateToShipmentOrders}
            navigateToShipmentOrderLegs={navigateToShipmentOrderLegs}
            appEnv={licenseContext.config?.app_env}
            navigateToPerformanceManagement={navigateToPerformanceManagement}
            useDispatchTableImport={useDispatchTableImport}
            navigateToDispatchTableImport={navigateToDispatchTableImport}
          />
        </Toolbar>
      )}
      {isSmallScreen && (
        <Toolbar
          sx={{
            display: {
              xs: 'flex',
              sm: 'none',
              md: 'none',
              lg: 'none',
              xl: 'none',
            }
          }}
        >
          <AppBarLogoComponent
            companies={companies}
            navigateToDashboard={navigateToDashboard}
            hideShowAllSelectMenuItem={hideShowAllSelectMenuItem}
            hideThisComponent={hideThisComponent}
          />
          <AppBarSmMenuComponent
            navigateToKpis={navigateToKpis}
            handlePlanningMenuOnClick={handlePlanningMenuOnClick}
            planningMenuAnchorEl={planningMenuAnchorEl}
            handlePlanningMenuOnClose={handlePlanningMenuOnClose}
            navigateToPlanningMap={navigateToPlanningMap}
            navigateToMap={navigateToMap}
            navigateToPlanningTimeline={navigateToPlanningTimeline}
            navigateToConvenienceDispatches={navigateToConvenienceDispatches}
            navigateToPlanningLane={navigateToPlanningLane}
            navigateToOrders={navigateToOrders}
            handleSettingMenuOnClick={handleSettingMenuOnClick}
            settingMenuAnchorEl={settingMenuAnchorEl}
            handleSettingMenuOnClose={handleSettingMenuOnClose}
            navigateToCourses={navigateToCourses}
            navigateToContracts={navigateToContracts}
            navigateToGarages={navigateToGarages}
            navigateToPartnerCompanies={navigateToPartnerCompanies}
            navigateToTrucks={navigateToTrucks}
            navigateToGroups={navigateToGroups}
            navigateToDrivers={navigateToDrivers}
            navigateToPlaces={navigateToPlaces}
            navigateToProducts={navigateToProducts}
            navigateToAreas={navigateToAreas}
            useOutsourcingContractPdf={useOutsourcingContractPdf}
            navigateToOutsourcingContractSettings={navigateToOutsourcingContractSettings}
            navigateToConfig={navigateToConfig}
            handleAccountMenuOnClick={handleAccountMenuOnClick}
            accountMenuAnchorEl={accountMenuAnchorEl}
            handleAccountMenuOnClose={handleAccountMenuOnClose}
            navigateToAccounts={navigateToAccounts}
            isAdmin={isAdmin}
            navigateToCompany={navigateToCompany}
            logout={logout}
            useTrunkTransportation={useTrunkTransportation}
            navigateToPlanningTrunkTransportation={navigateToPlanningTrunkTransportation}
            navigateToLongHaul={navigateToLongHaul}
            useLogForSales={useLogForSales}
            navigateToSalesOrders={navigateToSalesOrders}
            navigateToShipmentOrders={navigateToShipmentOrders}
            navigateToShipmentOrderLegs={navigateToShipmentOrderLegs}
            appEnv={licenseContext.config?.app_env}
            navigateToPerformanceManagement={navigateToPerformanceManagement}
            useDispatchTableImport={useDispatchTableImport}
            navigateToDispatchTableImport={navigateToDispatchTableImport}
          />
        </Toolbar>
      )}
    </AppBar>
  );
});

export default AppBarContainerComponent;
